import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";

const HERO_IMG =
  "https://images.pexels.com/photos/32619168/pexels-photo-32619168.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function CustomUniformsAuckland() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Custom Uniforms Auckland",
    serviceType: "Custom team uniform design and manufacturing",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
    areaServed: { "@type": "City", name: "Auckland" },
  };

  return (
    <>
      <SEOHead
        title="Custom Uniforms Auckland | Team Apparel Studio"
        description="Custom uniforms in Auckland for rugby, basketball, schools and church groups. Pasifika & Māori-led design studio. Free design concept within 24 hours."
        path="/custom-uniforms-auckland"
        image={HERO_IMG}
        jsonLd={jsonLd}
      />

      <SEOPageLayout
        heroEyebrow="Tāmaki Makaurau · Auckland"
        heroH1="Custom Uniforms in Auckland — Designed for Every Team"
        heroSub="Custom uniforms and team apparel for Auckland's rugby clubs, basketball teams, schools, churches and community groups. Pasifika & Māori-led studio, NZ-wide delivery."
        heroImage={HERO_IMG}
        heroImageAlt="Custom basketball uniforms designed for Auckland team"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Custom Uniforms Auckland" },
        ]}
      >
        <h2>Auckland's go-to studio for custom team uniforms</h2>
        <p>
          Tribal Designz is an Auckland-based custom apparel studio
          specialising in <strong>custom uniforms</strong> for sports teams,
          schools, churches and cultural groups across Tāmaki Makaurau. From
          South Auckland rugby clubs to North Shore basketball squads, from
          West Auckland touch teams to inner-city school cultural festivals,
          we've outfitted hundreds of groups across the region.
        </p>
        <p>
          Every kit we make is fully custom — no off-the-shelf templates, no
          generic clip art. Our designers sit down with you (or jump on a
          video call), understand who you are, what colours you wear, what
          symbols matter to your group, and then build a uniform that feels
          unmistakably yours. The result: <em>team uniforms Auckland</em>{" "}
          clubs and schools actually want to wear.
        </p>

        <h2>What we make</h2>
        <ul>
          <li><Link to="/custom-rugby-uniforms-nz">Custom rugby uniforms</Link> — jerseys, shorts, warm-ups, hoodies</li>
          <li><Link to="/custom-basketball-jerseys-nz">Custom basketball jerseys &amp; shorts</Link></li>
          <li>School cultural-group uniforms (kapa haka, Pasifika dance, choir)</li>
          <li>Church youth-group tees, polos &amp; hoodies</li>
          <li>Touch, league, sevens &amp; corporate sports kits</li>
          <li>Event polos, branded staff tees, supporter merch</li>
        </ul>

        <h2>Why Auckland teams choose Tribal Designz</h2>
        <p>
          We're not a print broker. We're a design-led studio that happens to
          also manufacture. That means every uniform we ship has had a
          designer — not a software template — shaping the layout, balancing
          the patterns, picking the typeface, and making sure your sponsor
          logos sit where they should. It's the same level of design care a
          professional rugby franchise gets, made accessible for community
          clubs.
        </p>
        <p>
          We're also Pasifika and Māori-led, which matters when culture is
          part of the brief. We know which patterns are appropriate for which
          contexts, how to honour rather than appropriate, and how to
          translate iconography into apparel that the older generation in
          your club will respect.
        </p>

        <h2>Serving teams across Auckland &amp; NZ</h2>
        <p>
          We work with clubs and schools right across the city — Manukau,
          Otara, Mangere, Otahuhu, Onehunga, Mt Roskill, Mt Albert,
          Henderson, Te Atatu, Massey, Glenfield, Takapuna, Albany, and
          everywhere in between. We deliver across Auckland and ship NZ-wide
          to Hamilton, Tauranga, Wellington, Christchurch and beyond.
        </p>

        <h2>Two ways to order</h2>
        <p>
          Most teams choose our <strong>Premium Custom Uniforms</strong>{" "}
          pathway: fully designed-with-you kits, 10+ unit minimum, 2–3 week
          turnaround, full sublimated print. For events and small groups
          who need something quickly, our{" "}
          <Link to="/fast-tshirt-printing-auckland">Fast Print (DTF) service</Link>{" "}
          delivers in as little as 72 hours with no minimum-order pressure.
        </p>

        <h2>Pricing &amp; lead times</h2>
        <p>
          Pricing depends on the garment, fabric weight, print complexity and
          quantity — but every quote is transparent and itemised. As a rough
          guide, premium custom rugby jerseys start around mid-range club
          pricing and drop significantly per-unit at higher volumes (40+
          kits). Standard turnaround is 2–3 weeks from artwork sign-off; rush
          options are available.
        </p>

        <h2>Get a free design concept</h2>
        <p>
          Send us your details below — team name, colours you'd like to use,
          rough quantity and deadline — and we'll come back within 24 hours
          with a free design concept and itemised quote. No commitment until
          you love what you see.
        </p>

        <p>
          Want to read more first?{" "}
          <Link to="/blog/best-custom-uniforms-rugby-auckland">
            Best custom uniforms for rugby teams in Auckland →
          </Link>
        </p>
      </SEOPageLayout>
    </>
  );
}
